/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { noop } from 'lodash';
import { ChangeEvent, FocusEvent } from 'react';

import Icon from '../Icon';
import { Colors, Border, Spacing } from '@zapier/theme';

const SIZE = 20;
enum INPUT_TYPE {
  checkbox = 'checkbox',
  radio = 'radio',
}

const Styles = {
  root: css`
    align-items: center;
    border-color: ${Colors.adamantium};
    border-radius: ${Border.radius.default};
    border-style: solid;
    border-width: ${Border.size.default};
    color: ${Colors.white};
    display: inline-flex;
    flex: none;
    height: ${SIZE}px;
    width: ${SIZE}px;
    justify-content: center;
    position: relative;
  `,
  radioButton: css`
    border-radius: 100%;
  `,
  checked: css`
    background-color: ${Colors.heroine};
    border: ${Border.size.default} solid ${Colors.heroine};
  `,
  disabled: (vacant: boolean) => [
    css`
      cursor: default;
    `,
    vacant &&
      css`
        opacity: 1;
      `,
    !vacant &&
      css`
        opacity: 0.25;
      `,
  ],
  input: css`
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    position: absolute;
    margin: ${Spacing.none};
    padding: ${Spacing.none};
    opacity: 0;
  `,
};

type ChangeDescription = {
  name: string;
  value: string;
  checked: boolean;
};

type Props = {
  checked?: boolean;
  indeterminate?: boolean;
  name?: string;
  id?: string;
  disabled?: boolean;
  value?: string;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange: (changed: ChangeDescription) => void;
  type: INPUT_TYPE;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-required'?: boolean | 'false' | 'true' | undefined;
};

const iconName = (props: Props) => {
  if (props.indeterminate) {
    return 'indeterminate';
  }

  if (props.checked) {
    return props.type === INPUT_TYPE.checkbox ? 'check-alt' : 'disc';
  }

  return 'disc';
};

/**
 * Renders a checkbox or a radio button.
 */
const BooleanInput = (props: Props) => {
  const wrapperCss = [
    Styles.root,
    props.type === INPUT_TYPE.radio && Styles.radioButton,
    (props.checked || props.indeterminate) && Styles.checked,
    props.disabled && Styles.disabled(!props.checked && !props.indeterminate),
  ];

  const iconScale = props.type === INPUT_TYPE.checkbox ? 0.5 : 0.3;

  const iconCss = css({
    height: SIZE * iconScale,
    width: SIZE * iconScale,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Use `checked` from the DOM node to ensure accuracy.
    // Using `this.props.checked` can be trickier because
    // we need to negate it and there may be a race condition
    // between the event and when the DOM is updated.
    const { checked, name, value } = e.target;

    props.onChange({
      checked,
      name,
      value,
    });
  };

  return (
    <span
      aria-checked={props.indeterminate ? 'mixed' : Boolean(props.checked)}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-required={props['aria-required']}
      css={wrapperCss}
      role={props.type}
    >
      <input
        aria-hidden={true}
        aria-label={props['aria-label']}
        checked={props.checked}
        css={Styles.input}
        disabled={props.disabled}
        id={props.id}
        name={props.name}
        onBlur={props.onBlur}
        onChange={handleChange}
        onFocus={props.onFocus}
        type={props.type}
        value={props.value}
      />
      <Icon css={iconCss} name={iconName(props)} size={12} />
    </span>
  );
};

BooleanInput.defaultProps = {
  disabled: false,
  indeterminate: false,
  onChange: noop,
  type: INPUT_TYPE.checkbox,
};

export default BooleanInput;
